import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LabelWithTip } from "@/components/ui/tooltip";
import StatsExplainer from "@/components/StatsExplainer";
import EntryGroupsSplit from "@/components/EntryGroupsSplit";
import HourlyTrendChart from "@/components/HourlyTrendChart";
import { formatPLN } from "@/lib/utils";
import type { MetricTipId } from "@/lib/metricsGlossary";

type DayPeriod = "today" | "yesterday";

const DAY_OPTIONS: { id: DayPeriod; label: string }[] = [
  { id: "today", label: "Dziś" },
  { id: "yesterday", label: "Wczoraj" },
];

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

export default function DayTrafficAnalysis() {
  const [period, setPeriod] = useState<DayPeriod>("today");

  const query = useQuery({
    queryKey: ["day-traffic", period],
    queryFn: () => api.siteAnalyticsDayTraffic(period),
    retry: false,
  });

  const data = query.data;
  const summary = data?.summary;
  const hours = data?.hourly?.hours ?? [];

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="dzien" />

      <div className="flex flex-wrap items-center gap-2">
        {DAY_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === period ? "default" : "outline"}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {data?.period?.label_pl ?? "czas PL"}
          {" · "}
          Godziny 00:00–23:00 (Europe/Warsaw)
        </span>
      </div>

      {query.isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Wczytywanie ruchu dnia…
          </CardContent>
        </Card>
      )}
      {query.isError && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nie udało się wczytać danych.
          </CardContent>
        </Card>
      )}

      {summary && (
        <>
          <EntryGroupsSplit
            paidCount={summary.paid_entries ?? 0}
            otherCount={summary.other_entries ?? 0}
            title="Wejścia tego dnia: płatne vs pozostałe"
            subtitle="?via=ad lub Social płatny · reszta = organiczne, direct, SEO…"
            showModeToggle={false}
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Kpi tipId="funnel_entry" value={String(summary.sessions)} label="Wizyty" />
            <Kpi tipId="cart_sessions_ours" value={String(summary.cart_sessions)} />
            <Kpi tipId="purchases_on_path" value={String(summary.purchase_sessions)} />
            <Kpi
              tipId="kpi_today"
              value={formatPLN(summary.easycart_revenue ?? 0)}
              label="Przychód EC"
            />
            <Kpi tipId="cart_to_purchase" value={pct(summary.cart_to_purchase_rate)} />
          </div>

          <HourlyTrendChart
            title="Ruch godzina po godzinie"
            subtitle="Wizyty, koszyki i zakupy na stronie (nasz licznik)"
            data={hours}
            mode="line"
            series={[
              { key: "sessions", label: "Wizyty", color: "hsl(var(--brand))" },
              { key: "cart_sessions", label: "Koszyki", color: "#6366f1" },
              {
                key: "purchase_sessions",
                label: "Zakupy (ścieżka)",
                color: "#22c55e",
                rightAxis: true,
              },
            ]}
          />

          <HourlyTrendChart
            title="Sprzedaż EasyCart godzinowo"
            subtitle="Opłacone zamówienia i przychód (źródło prawdy o kasie)"
            data={hours}
            mode="bar"
            series={[
              {
                key: "easycart_revenue",
                label: "Przychód",
                color: "hsl(var(--brand))",
                format: "pln",
              },
              {
                key: "easycart_paid_orders",
                label: "Zamówienia",
                color: "#22c55e",
                rightAxis: true,
              },
            ]}
          />

          <HourlyTrendChart
            title="Płatne vs pozostałe wejścia (godzinowo)"
            subtitle="Stack: reklama (?via=ad) vs reszta kanałów"
            data={hours}
            mode="bar"
            series={[
              { key: "paid_sessions", label: "Płatne", color: "#f59e0b", stackId: "entries" },
              { key: "other_sessions", label: "Pozostałe", color: "#94a3b8", stackId: "entries" },
            ]}
          />

          {data?.insights && data.insights.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Wnioski z dnia</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {data.insights.map((line, i) => (
                    <li key={i} className="flex gap-2">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({
  tipId,
  value,
  label,
}: {
  tipId: MetricTipId;
  value: string;
  label?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">
          <LabelWithTip tipId={tipId} label={label} />
        </div>
        <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}
